import '../staticForm/QuoteForm.scss';
import './productLayouts.scss';
import { EnrichmentError } from 'components/common-form/builder/form/enrichment-error';
import { GenericErrorBoundary } from 'components/common-form/builder/form/GenericErrorBoundary';
import { TopBar } from 'components/common-form/builder/form/node/block/TopBar';
import { BlockMetadataContext } from 'components/common-form/contexts/BlockMetadataContext';
import { DataApi, DataContext } from 'components/common-form/contexts/DataContext';
import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { WithPatchedFieldsContext } from 'components/common-form/contexts/WithPatchedFieldsContext';
import type { CrossBrowserState } from 'contexts/crossBrowserContext/CrossBrowserContext';
import { CrossBrowserContext } from 'contexts/crossBrowserContext/CrossBrowserContext';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import type { FormRecord } from 'models/formError';
import { useCallback, useMemo } from 'react';
import { FormFieldId } from 'state/formData';
import type { Collection } from 'state/utils/types';
import { z } from 'zod';

import { MainFormSkeleton } from '../common-form/components/mainFormSkeleton';
import { ProductViewContext } from '../common-form/contexts/ProductViewContext';
import { MainForm } from '../common-form/MainForm';
import { SidePanelContainer } from '../staticForm/sidePanelLayout/SidePanelContainer';
import type { DynamicFormProps } from './DynamicForm.connect';
import { connectDynamicForm } from './DynamicForm.connect';

const SalesCounterpart = z
  .object({
    id: z.string(),
    label: z.string(),
    eliotCode: z.optional(z.string()),
    countryResidenceCode: z.optional(z.string()),
  })
  .passthrough();

const Enrichment = z
  .object({
    ['SelectedOnBehalfClient' satisfies FormFieldId]: z.union([SalesCounterpart, z.null()]).optional(),
    ['ProductType' satisfies FormFieldId]: z.optional(z.string()),
    ['ProductSubtype' satisfies FormFieldId]: z.optional(z.string()),
    ['Wrapper' satisfies FormFieldId]: z.optional(z.string()),
  })
  .passthrough();

export function RawDynamicForm({
  blocksMetadata,
  enrichedData,
  fieldsMetadata,
  inputData,
  pendingEnrichmentData,
  formId,
  setFieldValue,
  isLoading,
  hideResetButton,
  onResetTabClicked,
  onRefreshDefaultDatesClicked,
  onRefreshScheduleClicked,
  onRollback,
  hideCloneButton,
  hideOpenInBatchWorkspaceButton,
  onTabCloned,
  hidePrivateMode,
  productDescription,
  validationErrors,
  isUserInternal,
  crossBrowserDirty,
  canReloadSession,
  canReset,
  canClone,
  isNotEditable,
  isTabFrozen,
  SidePanel,
  negotiationId,
}: DynamicFormProps) {
  const data = useMemo(
    () =>
      Enrichment.parse({
        ...enrichedData,
        ...pendingEnrichmentData,
        ...inputData,
      }),
    [enrichedData, pendingEnrichmentData, inputData],
  );

  const dataApi: DataApi = useMemo(() => ({ setFieldValue }), [setFieldValue]);
  const handleReload = useCallback(() => onRollback(negotiationId!), [negotiationId, onRollback]);
  const instrumentContractName = data['ProductSubtype' satisfies FormFieldId];
  const dataContext = useMemo(() => [data as Collection<Readonly<unknown>>, dataApi] as const, [data, dataApi]);

  const crossBrowserContext = useMemo<CrossBrowserState>(
    () => ({ isDirty: crossBrowserDirty, isLocked: isTabFrozen }),
    [isTabFrozen, crossBrowserDirty],
  );

  return (
    <CrossBrowserContext.Provider value={crossBrowserContext}>
      <ProductViewContext.Provider value="Default">
        <BlockMetadataContext.Provider value={blocksMetadata}>
          <FieldMetadataContext.Provider value={fieldsMetadata}>
            <DataContext.Provider value={dataContext}>
              <WithPatchedFieldsContext formId={formId}>
                <WithInvalidFieldsContext<FormRecord, QuoteKeys, QuoteKeysPrefix> validationErrors={validationErrors}>
                  <EnrichmentError />
                  <SidePanelContainer
                    topBar={
                      !isLoading && (
                        <TopBar
                          hideReset={hideResetButton}
                          canReset={canReset}
                          isOnBehalfUser
                          hideClone={hideCloneButton}
                          hidePrivateMode={hidePrivateMode}
                          hideOpenInBatchWorkspace={hideOpenInBatchWorkspaceButton}
                          onTabCloned={onTabCloned}
                          onResetTabClicked={onResetTabClicked}
                          onRefreshDefaultDatesClicked={onRefreshDefaultDatesClicked}
                          onRefreshScheduleClicked={onRefreshScheduleClicked}
                          canClone={canClone}
                          isNotEditable={isNotEditable}
                        />
                      )
                    }
                    sidePanel={
                      <GenericErrorBoundary key={formId} context={{ tabId: formId }}>
                        {SidePanel}
                      </GenericErrorBoundary>
                    }
                  >
                    {isLoading || !productDescription ? (
                      <MainFormSkeleton />
                    ) : (
                      <MainForm
                        tabId={formId}
                        isNotEditable={isNotEditable}
                        isUserInternal={isUserInternal}
                        product={instrumentContractName!}
                        productDescription={productDescription}
                        crossBrowserDirty={crossBrowserDirty}
                        workspaceType="dynamic"
                        onSessionReload={handleReload}
                        canReloadSession={canReloadSession}
                        quoteWorkspaceVersion="Advanced"
                        batchWorkspaceVersion="Advanced"
                        mustForceAdvancedMode
                      />
                    )}
                  </SidePanelContainer>
                </WithInvalidFieldsContext>
              </WithPatchedFieldsContext>
            </DataContext.Provider>
          </FieldMetadataContext.Provider>
        </BlockMetadataContext.Provider>
      </ProductViewContext.Provider>
    </CrossBrowserContext.Provider>
  );
}

RawDynamicForm.displayName = 'DynamicForm';

export const DynamicForm = connectDynamicForm(RawDynamicForm);
